import { SharedScreenState } from '@/types/screen';
import { combineReducers } from '@/utils/combineReducers';
import {
  ActionType,
  QR_CODE_FOREGROUND_COLOR_CHANGED,
  QR_CODE_BACKGROUND_COLOR_CHANGED,
  QR_CODE_WRAPPER_BACKGROUND_COLOR_CHANGED,
  ADD_STAGE_TEXT_ELEMENT,
  ADD_FAN_TEXT_ELEMENT,
  ADD_STAGE_IMAGE_ELEMENT,
  ADD_FAN_IMAGE_ELEMENT,
  SET_STAGE_BACKGROUND_PREVIEW,
  SET_FAN_BACKGROUND_PREVIEW,
  SAVE_STAGE_BACKGROUND_IMAGE,
  SAVE_FAN_BACKGROUND_IMAGE,
  HEADING_FONT_CHANGED,
  BODY_FONT_CHANGED,
} from './actions';
import {
  SCREEN_ELEMENT_SELECTED,
  SET_ACTIVE_ENGAGEMENT,
  SET_ENGAGEMENT_MODE,
  BACKGROUND_COLOR_CHANGED,
  SCREEN_ELEMENT_UPDATED,
  SCREEN_ELEMENT_DRAFT_UPDATED,
  FOREGROUND_COLOR_CHANGED,
  SCREEN_ELEMENT_DELETED,
} from '../sharedActions';
import { omit } from 'lodash';

export const screenThemeStateReducer = combineReducers<
  SharedScreenState,
  ActionType
>({
  eventId: eventIdReducer,
  savedTheme: savedThemeReducer,
  draftTheme: draftThemeReducer,
  selectedElementId: selectedElementIdReducer,
  activeEngagement: activeEngagementReducer,
  engagementMode: engagementModeReducer,
});

function eventIdReducer(state: SharedScreenState['eventId']) {
  return state;
}

function savedThemeReducer(
  state: SharedScreenState['savedTheme'],
  action: ActionType,
) {
  switch (action.type) {
    // Stage element actions
    case ADD_STAGE_TEXT_ELEMENT:
    case ADD_STAGE_IMAGE_ELEMENT: {
      return {
        ...state,
        stageElements: {
          ...state.stageElements,
          [action.payload.element.id]: action.payload.element,
        },
      };
    }

    // Fan element actions
    case ADD_FAN_TEXT_ELEMENT:
    case ADD_FAN_IMAGE_ELEMENT: {
      return {
        ...state,
        fanElements: {
          ...state.fanElements,
          [action.payload.element.id]: action.payload.element,
        },
      };
    }

    case SCREEN_ELEMENT_DELETED: {
      const updateKey = getElementArrayKey(state, action.payload.id);
      return {
        ...state,
        [updateKey]: omit(state[updateKey], action.payload.id),
      };
    }

    case SCREEN_ELEMENT_UPDATED: {
      const {
        element,
        element: { id },
      } = action.payload;

      const updateKey = getElementArrayKey(state, id);

      return {
        ...state,
        [updateKey]: {
          ...state[updateKey],
          [element.id]: element,
        },
      };
    }

    case HEADING_FONT_CHANGED: {
      return {
        ...state,
        headingFontFamily: action.payload.fontFamily ?? [],
      };
    }

    case BODY_FONT_CHANGED: {
      return {
        ...state,
        bodyFontFamily: action.payload.fontFamily ?? [],
      };
    }

    case SAVE_STAGE_BACKGROUND_IMAGE: {
      return {
        ...state,
        stageBackgroundImage: action.payload.backgroundImage,
      };
    }

    case SAVE_FAN_BACKGROUND_IMAGE: {
      return {
        ...state,
        fanBackgroundImage: action.payload.backgroundImage,
      };
    }

    case BACKGROUND_COLOR_CHANGED: {
      return {
        ...state,
        backgroundColor: action.payload.backgroundColor,
      };
    }

    case FOREGROUND_COLOR_CHANGED: {
      return {
        ...state,
        foregroundColor: action.payload.foregroundColor,
      };
    }

    // Stage-specific QR code colors
    case QR_CODE_FOREGROUND_COLOR_CHANGED: {
      return {
        ...state,
        qrForegroundColor: action.payload.qrForegroundColor,
      };
    }
    case QR_CODE_BACKGROUND_COLOR_CHANGED: {
      return {
        ...state,
        qrBackgroundColor: action.payload.qrBackgroundColor,
      };
    }
    case QR_CODE_WRAPPER_BACKGROUND_COLOR_CHANGED: {
      return {
        ...state,
        qrWrapperBackgroundColor: action.payload.qrWrapperBackgroundColor,
      };
    }

    default:
      return state;
  }
}

function draftThemeReducer(
  state: SharedScreenState['draftTheme'],
  action: ActionType,
) {
  switch (action.type) {
    case SET_STAGE_BACKGROUND_PREVIEW: {
      return {
        ...state,
        stageBackgroundImage: action.payload.backgroundImage,
      };
    }

    case SET_FAN_BACKGROUND_PREVIEW: {
      return {
        ...state,
        fanBackgroundImage: action.payload.backgroundImage,
      };
    }

    case SAVE_STAGE_BACKGROUND_IMAGE: {
      return {
        ...state,
        stageBackgroundImage: undefined,
      };
    }

    case SAVE_FAN_BACKGROUND_IMAGE: {
      return {
        ...state,
        fanBackgroundImage: undefined,
      };
    }

    case SCREEN_ELEMENT_DRAFT_UPDATED: {
      const { element } = action.payload;
      const screenType =
        element.__typename === 'ScreenElement' ? 'stage' : 'fan';
      const updateKey = (screenType + 'Elements') as
        | 'stageElements'
        | 'fanElements';
      return {
        ...state,
        [updateKey]: {
          ...state[updateKey],
          [element.id]: element,
        },
      };
    }

    default:
      return state;
  }
}

function getElementArrayKey(
  state: SharedScreenState['savedTheme'],
  id: string,
) {
  // id should be key in either the stageElements object or fanElements object
  let updateKey: 'stageElements' | 'fanElements';
  if (id in state.stageElements) {
    updateKey = 'stageElements';
  } else if (id in state.fanElements) {
    updateKey = 'fanElements';
  } else {
    throw new Error(`Element with id ${id} not found`);
  }
  return updateKey;
}

function selectedElementIdReducer(
  state: SharedScreenState['selectedElementId'],
  action: ActionType,
) {
  switch (action.type) {
    case ADD_STAGE_TEXT_ELEMENT:
    case ADD_STAGE_IMAGE_ELEMENT:
    case ADD_FAN_TEXT_ELEMENT:
    case ADD_FAN_IMAGE_ELEMENT: {
      return action.payload.element.id;
    }
    case SCREEN_ELEMENT_SELECTED: {
      return action.payload.id;
    }
    default:
      return state;
  }
}

function activeEngagementReducer(
  state: SharedScreenState['activeEngagement'],
  action: ActionType,
) {
  switch (action.type) {
    case SET_ACTIVE_ENGAGEMENT: {
      return action.payload.engagement;
    }
    default:
      return state;
  }
}

function engagementModeReducer(
  state: SharedScreenState['engagementMode'],
  action: ActionType,
) {
  switch (action.type) {
    case SET_ENGAGEMENT_MODE: {
      return action.payload.mode;
    }
    default:
      return state;
  }
}
